import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { ctaLabel, ctaDomain, performAdCta } from "@/lib/adCta";
import { AdLeadDialog } from "@/components/AdLeadDialog";
import { ChevronRight } from "lucide-react";

// Facebook-style CTA footer shown at the bottom of a sponsored post
// (small label + headline + description on the left, CTA button on the right).
export const AdCtaFooter = ({ ad, author }) => {
  const nav = useNavigate();
  const { openChat } = useApp();
  const ref = useRef(null);
  const [seen, setSeen] = useState(false);
  const [leadOpen, setLeadOpen] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || seen || !ad?.id) return;
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) {
        setSeen(true);
        api.post(`/ads/${ad.id}/impression`).catch(() => {});
        io.disconnect();
      }
    }, { threshold: 0.5 });
    io.observe(el);
    return () => io.disconnect();
  }, [ad?.id, seen]);

  if (!ad) return null;
  const hasButton = ad.type && ad.type !== "none";
  if (!hasButton && !ad.headline && !ad.description) return null;

  return (
    <div ref={ref} className="mt-3 rounded-2xl border flex items-center gap-3 p-3" style={{ borderColor: "var(--yalola-border)", background: "var(--yalola-surface-alt)" }} data-testid={`ad-cta-${ad.id}`}>
      <div className="min-w-0 flex-1">
        <div className="text-[10px] font-bold uppercase tracking-wide" style={{ color: "var(--yalola-text-muted)" }}>{ctaDomain(ad)}</div>
        {ad.headline && <div className="text-sm font-bold leading-snug truncate">{ad.headline}</div>}
        {ad.description && <div className="text-xs mt-0.5 line-clamp-2" style={{ color: "var(--yalola-text-muted)" }}>{ad.description}</div>}
      </div>
      {hasButton && (
        <button
          type="button"
          onClick={() => performAdCta(ad, { nav, openChat, author, openLead: () => setLeadOpen(true) })}
          className="pill pill-primary shrink-0 whitespace-nowrap"
          data-testid={`ad-cta-btn-${ad.id}`}
        >
          {ctaLabel(ad)} <ChevronRight size={15} className="ml-0.5" />
        </button>
      )}
      <AdLeadDialog ad={ad} open={leadOpen} onOpenChange={setLeadOpen} />
    </div>
  );
};

export default AdCtaFooter;
